// A team's page, and the chart of its season, as data. P6-329c (GN-2c).
//
// ═══════════════════════════════════════════════════════════════════════════
// THE LINE IS GAMES OVER .500, NOT A WIN PERCENTAGE
// ═══════════════════════════════════════════════════════════════════════════
//
// The design plots `w - l` after each game (HANDOFF-seasons.md, flag 8): the shape reads the same as a win-percentage
// line and a flat .500 is a straight line you can see. The caption gives the best mark and when it came. Every row of
// the standings and every tile of the teams grid draws the same quantity on ONE league-wide scale
// (`chartMath.js::trajectoryDomain`), so a line in one row can be compared with a line in another; the team page
// draws the full chart with its own axis.
//
// ═══════════════════════════════════════════════════════════════════════════
// EVERY TEAM HAS A TEAM FILE, SO THE DESIGN'S STANDINGS-ROW FALLBACK IS NOT BUILT
// ═══════════════════════════════════════════════════════════════════════════
//
// The design drew a team page for a team with no file out of its standings row (`PPG / Opp PPG / Diff`) because its
// samples had one team file. The backfill has thirty a season (`RECONCILIATION-seasons.md`: *"keep it only if it costs
// nothing; don't build it for its own sake"*), so a team with no file is a team page that is not there and the standings
// row is only ever the SOURCE OF THE RANK: `meta`, the streak, the last ten and the conference and division records.

import { monthLabels, niceTicks, overSeries } from './chartMath.js'
import { STAT, fmt, isNumber, monthName, ord, rec, sgn, wdDayMon, wl } from './historyFormat.js'

/** Games over .500 as the readout and the caption write it: `+7`, a minus sign and `3`, or `at .500`. */
export const overText = (value) => (value === 0 ? 'at .500' : `${sgn(value)}${Math.abs(value)}`)

/**
 * The line of a team's season as games over .500, with its axis, its months, and the readout for one game.
 *
 * `selected` is the game's index, or `null` for the default, the last game of the season. Returns `null` for a
 * trajectory with fewer than two games: a line needs two points, and the page draws no chart rather than a dot.
 */
export function trajectoryModel({ trajectory, selected }) {
  const { dates, wins, losses, over } = overSeries(trajectory)
  const count = over.length
  if (count < 2) return null
  const axis = niceTicks(Math.min(0, ...over), Math.max(0, ...over), 4)
  const span = axis.hi - axis.lo
  const x = (index) => (index / (count - 1)) * 100
  const y = (value) => ((axis.hi - value) / span) * 100
  const d = over.map((value, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(2)} ${y(value).toFixed(2)}`).join(' ')
  const index = selected === null || selected === undefined ? count - 1 : Math.max(0, Math.min(count - 1, selected))

  // The best mark is the FIRST game that reaches it.
  let peak = 0
  over.forEach((value, i) => {
    if (value > over[peak]) peak = i
  })

  return {
    count,
    selected: index,
    d,
    area: `${d} L${x(count - 1).toFixed(2)} ${y(0).toFixed(2)} L0 ${y(0).toFixed(2)} Z`,
    ticks: axis.ticks.map((tick) => ({
      top: y(tick),
      label: tick === 0 ? '.500' : `${sgn(tick)}${Math.abs(tick)}`,
      zero: tick === 0,
    })),
    months: monthLabels(dates, x, monthName),
    selectedLeft: x(index),
    selectedTop: y(over[index]),
    kicker: `Game ${index + 1} of ${count}`,
    read: `${wdDayMon(dates[index])} · ${wl(wins[index], losses[index])} · ${overText(over[index])}`,
    caption: `Games over .500 · best ${overText(over[peak])} after game ${peak + 1}`,
    peak,
    label: 'Win–loss record by game. Use arrow keys to move between games.',
  }
}

/** A team's standings row from the standings file, with the conference it is in; `null` if it is not in one. */
export function standingsRow(standings, teamId) {
  for (const [conference, rows] of Object.entries(standings?.conferences ?? {})) {
    const row = (Array.isArray(rows) ? rows : []).find((entry) => entry?.team_id === teamId)
    if (row) return { ...row, conference }
  }
  return null
}

/** The roster's columns: GP, PTS, REB and AST, with MIN and FG% from 560px wide. */
export function rosterColumns(wide) {
  return wide ? ['gp', 'min', 'pts', 'reb', 'ast', 'fg_pct'] : ['gp', 'pts', 'reb', 'ast']
}

/** The roster, sorted by points, as rows of cells in `rosterColumns`'s order. */
export function rosterModel(roster, wide) {
  if (!Array.isArray(roster) || roster.length === 0) return null
  const columns = rosterColumns(wide)
  return {
    count: `${roster.length} players`,
    heads: columns.map((key) => STAT[key]),
    columns: columns.length,
    rows: [...roster]
      .sort((a, b) => (b.per_game?.pts ?? 0) - (a.per_game?.pts ?? 0))
      .map((player) => ({
        playerId: player.player_id,
        name: player.name,
        sub: [player.pos, player.traded ? 'Traded' : null].filter(Boolean).join(' · '),
        cells: columns.map((key) => (key === 'gp' ? String(player.gp) : fmt(key, player.per_game?.[key]))),
      })),
  }
}

/**
 * Everything a team's page draws, or `null` for a team with no file.
 *
 * - `file`       the team's file for the season
 * - `row`        its standings row (`standingsRow`), or `null`
 * - `standings`  the season's standings file, for the trajectory when the team's own file has none
 * - `wide`       is the viewport at least 560px, which decides the roster's columns
 * - `nameFor`    the team's full name from the board's table
 */
export function teamModel({ file, row, standings, wide, nameFor, teamId }) {
  if (!file) return null
  const line = file.line
  const name = file.name ?? nameFor(teamId, file.abbr)
  const meta = [
    wl(row?.w ?? line.w, row?.l ?? line.l),
    row ? `${ord(row.rank)} in the ${row.conference}` : (file.conference ?? null),
    row ? `${ord(row.division_rank)} ${row.division}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const records = []
  const log = file.game_log
  if (log && Array.isArray(log.rows)) {
    const column = {}
    log.columns.forEach((columnName, index) => {
      column[columnName] = index
    })
    for (const [code, label] of [
      ['H', 'Home'],
      ['A', 'Road'],
      ['N', 'Neutral'],
    ]) {
      const games = log.rows.filter((game) => game[column.loc] === code)
      if (games.length > 0) {
        records.push({
          label,
          value: wl(games.filter((game) => game[column.wl] === 'W').length, games.filter((game) => game[column.wl] === 'L').length),
        })
      }
    }
  } else if (row) {
    records.push({ label: 'Home', value: rec(row.home) }, { label: 'Road', value: rec(row.road) })
  }
  if (row) {
    records.push(
      { label: 'Opp PPG', value: Number(row.opp_ppg).toFixed(1) },
      { label: 'Last 10', value: rec(row.l10) },
      { label: 'Streak', value: row.streak },
      { label: 'Conf', value: rec(row.conf) },
      { label: 'Div', value: rec(row.div) },
    )
  }

  const ratings = file.ratings
  const hasRatings = Boolean(ratings && isNumber(ratings.off_rating))
  const trajectory = file.trajectory ?? standings?.trajectories?.[teamId] ?? null

  return {
    teamId,
    abbr: file.abbr,
    name,
    meta,
    big: ['pts', 'reb', 'ast'].map((key) => ({ key, label: STAT[key], value: fmt(key, line[key]) })),
    small: ['fg_pct', 'fg3_pct', 'ft_pct', 'stl', 'blk', 'tov']
      .filter((key) => line[key] != null)
      .map((key) => ({ key, label: STAT[key], value: fmt(key, line[key]) })),
    records,
    ratings: hasRatings
      ? [
          { label: 'Off', value: ratings.off_rating.toFixed(1) },
          { label: 'Def', value: ratings.def_rating.toFixed(1) },
          { label: 'Net', value: `${sgn(ratings.net_rating)}${Math.abs(ratings.net_rating).toFixed(1)}` },
        ]
      : null,
    pace: isNumber(ratings?.pace) ? `Pace ${ratings.pace.toFixed(1)}` : '',
    trajectory,
    roster: rosterModel(file.roster, wide),
    asOf: file.as_of ?? null,
    source: file.source ?? null,
  }
}
